<template>
	<div class="wall-vote fc">
		<transition name="fade" mode="out-in">
			<Vote v-if="voting" hide-ui wall @submitted="onSubmitted" />
			<div v-else class="fc">
				<Avatar ref="avatar" class="wall-vote__avatar" :radius="[160, 80]" />
				<transition name="fade">
					<div v-if="prevote" class="wall-vote__prevote">
						<ButtonGroup wall>
							<button class="btn" @mousedown="doVoting">Let's vote!</button>
						</ButtonGroup>
					</div>
				</transition>
			</div>
		</transition>
		<div class="wall-vote__info">
			<transition name="slide-up" mode="out-in">
				<div v-if="voting">
					<Instructions />
					<VoteProgress :height="1" />
				</div>
			</transition>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Avatar from '@/components/avatar/Avatar'
import Instructions from '@/components/wall/Instructions'
import ButtonGroup from '@/components/main/ButtonGroup'
import Vote from '@/components/vote/Vote'
import VoteProgress from '@/components/vote/VoteProgress'

import { enums } from 'protoswipe-shared'
const { Dialogue } = enums

const CAMPAIGN_REFRESH_MS = 60000

const TIMEOUT_PREVOTE = 10000
const TIMEOUT_VOTING = 60000
const TIMEOUT_RESET = 2000
const TIMEOUT_IDLE_ACTION = 8000

const MOVE_SPD = 400
const LOOK_SPD = 0.1
const LOOK_MOVE_THRESHOLD = 2

export default {
	name: 'WallVote',
	props: {
		mouse: Object
	},
	data() {
		return {
			interval: null,
			state: 'idle',
			transitioning: true,
			idleTimeout: null,
			idleActionTimeout: null,
			lookVec: [0, 0],
			lookTarget: [0, 0]
		}
	},
	computed: {
		...mapGetters({
			interactive: 'campaign/allowVoting'
		}),
		idle() {
			return this.state === 'idle'
		},
		prevote() {
			return this.state === 'prevote'
		},
		voting() {
			return this.state === 'voting'
		},
		idleTimeoutMs() {
			switch (this.state) {
				case 'prevote':
					return TIMEOUT_PREVOTE
				case 'voting':
					return TIMEOUT_VOTING
				default:
					return 0
			}
		}
	},
	methods: {
		...mapActions({
			refreshCampaign: 'campaign/load',
			refreshConfig: 'config/get',
			startSession: 'session/start'
		}),
		onSubmitted(success) {
			if (success) this.doIdle()
		},
		resetLookTarget() {
			const target = [window.innerWidth / 2, window.innerHeight]
			this.lookVec = [...target]
			this.lookTarget = [...target]
		},
		doIdle() {
			this.cancelIdleTimeouts()
			this.transitioning = true
			this.state = 'idle'
			this.resetLookTarget()

			const { avatar } = this.$refs
			if (avatar) {
				avatar.speechStop()
				avatar.resetEyes()
				avatar.eShock()
				avatar.moveToCenter({ duration: MOVE_SPD }).then(avatar.reset)
			}

			this.idleTimeout = window.setTimeout(() => {
				this.transitioning = false
				this.doIdleAction(false)
			}, TIMEOUT_RESET)
		},
		async doIdleAction(move = true) {
			const { avatar } = this.$refs
			if (avatar) {
				// Hide current speech
				await avatar.speechStop()

				// Move somewhere random then speak an idle phrase
				if (move) {
					avatar.eShock()
					await avatar.moveToRandom({ look: true })
				}
				avatar.speechAdd(Dialogue.wall_idle)
				avatar.speechStart()

				// Queue up another idle action
				this.idleActionTimeout = window.setTimeout(
					this.doIdleAction,
					TIMEOUT_IDLE_ACTION
				)
			}
		},
		async doPrevote() {
			const { avatar } = this.$refs
			if (avatar) {
				// Start transition, stop speaking and move avatar
				this.cancelIdleTimeouts()
				this.transitioning = true
				avatar.speechStop()
				avatar.eShock()
				await avatar.moveTo([0, -150], { duration: MOVE_SPD })

				// Stop transition, speak a wall idle prompt and update state
				this.transitioning = false
				avatar.reset()
				avatar.speechAdd(Dialogue.wall_interact)
				avatar.speechStart()
				this.state = 'prevote'
				this.setIdleTimeout()
			}
		},
		async doVoting() {
			const opts = { force: true }
			const success = await this.startSession(opts)
			const { avatar } = this.$refs

			if (success && avatar) {
				// Start transition, stop speaking and make avatar leave
				this.cancelIdleTimeouts()
				this.transitioning = true
				avatar.speechStop()
				await avatar.aLeave()

				// Switch to voting state
				this.transitioning = false
				this.state = 'voting'
				this.setIdleTimeout()
			}
		},
		cancelIdleTimeouts() {
			window.clearTimeout(this.idleTimeout)
			window.clearTimeout(this.idleActionTimeout)
		},
		setIdleTimeout() {
			// Clear existing idle timeout and set it again
			this.cancelIdleTimeouts()
			this.idleTimeout = window.setTimeout(this.doIdle, this.idleTimeoutMs)
		},
		onMove({ x, y }) {
			if (this.transitioning) return
			if (this.idle) {
				this.doPrevote()
			} else {
				// Track mouse with eyes
				this.lookTarget = [x, y]
				this.setIdleTimeout()
			}
		},
		refreshData() {
			this.refreshConfig()
			if (this.state === 'idle') {
				this.refreshCampaign()
			}
		}
	},
	watch: {
		mouse(next) {
			// Do nothing if not currently interactive (voting disabled by admin)
			if (this.interactive) this.onMove(next)
		}
	},
	tick() {
		const { avatar } = this.$refs
		if (avatar && !this.idle) {
			// Ease look position to look target
			this.lookVec = this.lookVec.map((x, i) => {
				const tx = this.lookTarget[i]
				const dx = tx - x
				return Math.abs(dx) > LOOK_MOVE_THRESHOLD ? x + dx * LOOK_SPD : tx
			})

			// Only update if target moved
			if (this.lookVec.some((x, i) => x !== this.lookTarget[i])) {
				avatar.lookAtWorld(this.lookVec, { snap: true })
			}
		}
	},
	created() {
		this.doIdle()
		this.resetLookTarget()

		// Interval for refreshing campaign details periodically
		this.interval = window.setInterval(this.refreshData, CAMPAIGN_REFRESH_MS)
	},
	beforeDestroy() {
		this.cancelIdleTimeouts()
		window.clearInterval(this.interval)
	},
	components: {
		Avatar,
		Instructions,
		ButtonGroup,
		Vote,
		VoteProgress
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/components';
@import '~scss/transitions';

@include fade($spd-wall);
@include slide-up($spd-wall);

.fc {
	@extend %flex-col;
	position: relative;
	flex-grow: 1;
}

.btn {
	@include button($blue-48, $white, $white, $blue-73, 8rem);
	font-size: 2rem;
	line-height: normal;
}

.wall-vote {
	$avatar-pad-bottom: 4rem;

	&__avatar {
		flex-grow: 1;
		margin-bottom: $avatar-pad-bottom;
	}
	&__prevote {
		position: absolute;
		bottom: $avatar-pad-bottom;
		left: 0;
		width: 100%;
		margin: -2rem 0;
	}
	&__info {
		flex-shrink: 0;
		height: 9rem;
	}
}
</style>
